import React, { FormEvent, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { useLocation } from 'react-router';
import { navigationService, userAuthenticator } from 'src/config/rootService';
import * as validator from 'src/shared/helpers/emailValidator';

import logoImg from 'src/public/img/mh-logo-text.svg';
import { Button, ButtonGroup, Input, InputType } from '@modernhealth/vital-components';
import { color, font } from 'src/shared/styles/variables';
import { config } from 'src/config/config';

export enum SingleSignOnFormTestId {
    EmailField = 'SingleSignOnFormTestId.EmailField',
}

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

const Title = styled.h1(
    {
        color: color.ebonyClay,
        lineHeight: '28px',
        textAlign: 'left',
        paddingBottom: '16px',
        margin: 0,
        width: '100%',
    },
    font.heavy(font.size.extraLarge),
);

const ErrorBanner = styled.div(
    {
        width: '100%',
        color: color.blueBayoux,
        lineHeight: '24px',
        paddingBottom: '8px',
        marginBottom: 0,
        textAlign: 'left',
        backgroundColor: color.lightSteelBlue,
        padding: 10,
        borderRadius: 4,
    },
    font.book(font.size.medium),
);

const FormContainer = styled.form({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: '112px',
    justifyContent: 'center',
    flexGrow: 1,
});

const InputContainer = styled.div({
    width: '100%',
    paddingBottom: '40px',
    '& > *': {
        paddingTop: '16px',
    },
});

const ButtonsContainer = styled.div({
    width: '100%',
});

interface Response {
    fromPath?: string;
}

const SingleSignOnForm: React.FC = () => {
    const { t } = useTranslation();

    const location = useLocation<Response>();

    const [state, setState] = useState({
        email: '',
    });

    const [errorMessage, setErrorMessage] = useState('');

    const { email } = state;

    const isTryingToRegister = location.state?.fromPath === 'revamp-signup';

    const handleEmailChange = (value: string) => {
        setState({
            ...state,
            email: value.trim(),
        });

        if (errorMessage) {
            setErrorMessage('');
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        try {
            await userAuthenticator.singleSignOnRequest({ email: state.email });
        } catch (error) {
            setErrorMessage(`${error}`);
        }
    };

    const validateForm = (): boolean => validator.isEmailValid(email);

    const handleGoBack = () => navigationService.goBack();

    const defineEmailErrors = () => {
        if (!email) {
            setErrorMessage(t('auth:registration_form.errors.emailMissing'));
        }

        if (email && !validator.isEmailValid(email)) {
            setErrorMessage(t('auth:registration_form.errors.emailInvalid'));
        }
        return errorMessage;
    };

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />

            <Container>
                <FormContainer onSubmit={handleSubmit}>
                    <Title>
                        {isTryingToRegister
                            ? t('auth:single_sign_on_form.revamp.register_title')
                            : t('auth:single_sign_on_form.revamp.login_title')}
                    </Title>
                    {errorMessage && <ErrorBanner>{errorMessage}</ErrorBanner>}

                    <InputContainer>
                        <Input
                            required
                            fieldId="email"
                            testID={SingleSignOnFormTestId.EmailField}
                            value={email}
                            type={InputType.TextField}
                            label={t('auth:registration_form.labels.email')}
                            onChange={value => handleEmailChange(value.toString())}
                            onBlur={defineEmailErrors}
                        />
                    </InputContainer>

                    <ButtonsContainer>
                        <ButtonGroup>
                            <Button onClick={handleGoBack} variant="secondary" size="large">
                                {t('auth:consent_form_page.back')}
                            </Button>
                            <Button size="large" disabled={!validateForm()} type="submit">
                                {t('auth:consent_form_page.continue')}
                            </Button>
                        </ButtonGroup>
                    </ButtonsContainer>
                </FormContainer>
            </Container>
        </>
    );
};

export default SingleSignOnForm;
